// =============================================================================
// src/lib/pwa.js - install to home screen + portal push notifications.
//
//   registerServiceWorker()      main.jsx, production builds only
//   installState()               "installed" | "prompt" | "ios" | "manual" | "unsupported"
//   promptInstall()              shows the browser's install dialog (Android/desktop Chrome/Edge)
//   onInstallChange(cb)          re-render when the install prompt becomes available
//   pushState()                  "on" | "off" | "blocked" | "needs-install" | "unsupported"
//   enablePush(lang) / disablePush()
//   enableStaffPush() / disableStaffPush()   ride alerts for care team drivers
//                                (staff-push edge function, Oct 2026)
//
// public/sw.js caches static files only and shows the notifications. Device
// registration goes through the portal-push edge function; the text shown on
// the device is decided by push-send (practice name + "something new", no
// health details).
// =============================================================================
import { supabase } from "./supabaseClient";

let deferredPrompt = null;
const listeners = new Set();
const notify = () => listeners.forEach((cb) => { try { cb(); } catch (e) { /* ignore */ } });

export function registerServiceWorker() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  window.addEventListener("beforeinstallprompt", (e) => {
    // Keep the event so the portal can offer its own "Add to home screen" button.
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });
  window.addEventListener("appinstalled", () => { deferredPrompt = null; notify(); });
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch((e) => console.warn("[pwa] service worker not registered:", e?.message || e));
  });
}

export function onInstallChange(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPadOS reports itself as a Mac; touch support tells them apart.
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

export function installState() {
  if (typeof window === "undefined") return "unsupported";
  if (isStandalone()) return "installed";
  if (deferredPrompt) return "prompt";
  if (isIOS()) return "ios";
  return "manual";
}

export async function promptInstall() {
  if (!deferredPrompt) return false;
  const e = deferredPrompt;
  deferredPrompt = null;
  e.prompt();
  const choice = await e.userChoice.catch(() => null);
  notify();
  return choice?.outcome === "accepted";
}

function pushSupported() {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

async function currentSubscription() {
  const reg = await navigator.serviceWorker.getRegistration();
  return reg ? reg.pushManager.getSubscription() : null;
}

export async function pushState() {
  // iPhone and iPad only allow web notifications from an app on the home screen.
  if (isIOS() && !isStandalone()) return "needs-install";
  if (!pushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "blocked";
  const sub = await currentSubscription().catch(() => null);
  return sub && Notification.permission === "granted" ? "on" : "off";
}

async function callPush(body, fn = "portal-push") {
  const { data, error } = await supabase.functions.invoke(fn, { body });
  if (error) {
    let detail = null;
    try { detail = await error.context.json(); } catch (e) { /* ignore */ }
    throw new Error(detail?.error || error.message || "push request failed");
  }
  return data;
}

function urlBase64ToUint8Array(b64) {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

// Returns the new state. Throws with a message on failure.
export async function enablePush(lang, fn = "portal-push") {
  if (!pushSupported()) return "unsupported";
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "blocked" : "off";
  const reg = (await navigator.serviceWorker.getRegistration()) || (await navigator.serviceWorker.register("/sw.js"));
  await navigator.serviceWorker.ready;
  const { public_key } = await callPush({ action: "config" }, fn);
  if (!public_key) throw new Error("not_configured");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(public_key) });
  await callPush({ action: "subscribe", subscription: sub.toJSON(), lang: lang === "es" ? "es" : "en" }, fn);
  return "on";
}

export async function disablePush(fn = "portal-push") {
  const sub = await currentSubscription().catch(() => null);
  if (!sub) return "off";
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => {});
  await callPush({ action: "unsubscribe", endpoint }, fn);
  return "off";
}

// Staff (care team drivers): same device registration, through staff-push.
export const enableStaffPush = () => enablePush("en", "staff-push");
export const disableStaffPush = () => disablePush("staff-push");

// Keep the device's language in step with the portal language switch.
export async function refreshPushLanguage(lang) {
  if (!pushSupported() || Notification.permission !== "granted") return;
  const sub = await currentSubscription().catch(() => null);
  if (sub) await callPush({ action: "subscribe", subscription: sub.toJSON(), lang: lang === "es" ? "es" : "en" }).catch(() => {});
}
